import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Emoji } from "../../../ui/components/Emoji";
import { Button } from "../../../ui/components/Button";
import { cn } from "../../../ui/utils/cn";
import { useGetEmojiSelectorQuery, useClaimEmojiMutation } from "../api/emojiApi";
import type { SelectorEmoji } from "../types/emojiTypes";
import { categoryIcon, selectorEmojiMatches } from "./emojiCatalogShared";

export type EmojiSelectorMode = "claim" | "pick";

export interface EmojiSelectorProps {
  /**
   * "claim" — claim your permanent signature (writes via /emoji/claim).
   * "pick"  — choose an emoji (card-of-the-day / revive); calls onSelect, no write.
   */
  mode?: EmojiSelectorMode;
  /** pick mode: fired when the user taps an emoji. */
  onSelect?: (emoji: string) => void;
  /** claim mode: fired after a successful permanent claim. */
  onClaimed?: (emoji: string) => void;
  /** pick mode: pre-highlight the currently selected emoji. */
  selectedEmoji?: string | null;
  /** pick mode: when true, emojis already used in a past AOTD are selectable (revive). */
  allowRevive?: boolean;
  className?: string;
}

const SEARCH_RENDER_CAP = 240;

const EmojiSelector: React.FC<EmojiSelectorProps> = ({
  mode = "pick",
  onSelect,
  onClaimed,
  selectedEmoji = null,
  allowRevive = false,
  className,
}) => {
  const { data, isLoading, isError } = useGetEmojiSelectorQuery();
  const [claimEmoji, { isLoading: claiming }] = useClaimEmojiMutation();

  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null); // claim mode: awaiting confirm

  const me = data?.data?.me;
  const categories = useMemo(() => data?.data?.categories ?? [], [data]);
  const total = data?.data?.total ?? 0;

  const currentCat =
    activeCat && categories.some((c) => c.category === activeCat)
      ? activeCat
      : categories[0]?.category ?? null;

  const searching = search.trim().length > 0;

  // full set is ~1,900 — render one category at a time, or capped search hits
  const visible = useMemo(() => {
    if (searching) {
      let budget = SEARCH_RENDER_CAP;
      const out: Array<{ category: string; emojis: SelectorEmoji[] }> = [];
      for (const c of categories) {
        if (budget <= 0) break;
        const hits = c.emojis.filter((e) => selectorEmojiMatches(e, c.category, search));
        if (hits.length > 0) {
          out.push({ category: c.category, emojis: hits.slice(0, budget) });
          budget -= hits.length;
        }
      }
      return out;
    }
    const cat = categories.find((c) => c.category === currentCat);
    return cat ? [cat] : [];
  }, [categories, currentCat, search, searching]);

  const totalShown = useMemo(
    () => visible.reduce((n, c) => n + c.emojis.length, 0),
    [visible],
  );

  const handleClick = (e: SelectorEmoji): void => {
    if (mode === "claim") {
      if (me?.hasSignature) return; // immutable
      if (e.taken || e.reserved) return;
      setPending(e.e);
      return;
    }
    // pick mode — taken/reserved emojis stay pickable for AOTD inputs
    // (identity ownership ≠ art use); only the user's own burn blocks
    if (e.used && !allowRevive) {
      toast.info("You've already used this emoji. Play a Reclamation card to use it again.");
      return;
    }
    onSelect?.(e.e);
  };

  const doClaim = async (): Promise<void> => {
    if (!pending) return;
    try {
      await claimEmoji({ emoji: pending }).unwrap();
      toast.success(`${pending} is now yours — forever.`);
      onClaimed?.(pending);
      setPending(null);
    } catch (err: any) {
      const msg = err?.data?.error || err?.data?.message || "Could not claim that emoji.";
      toast.error(msg);
    }
  };

  if (isLoading) {
    return <div className={cn("p-4 text-sm text-gray-500", className)}>Loading emojis…</div>;
  }
  if (isError) {
    return (
      <div className={cn("p-4 text-sm text-pink", className)}>Failed to load the emoji registry.</div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* header / signature notice */}
      {mode === "claim" && me?.hasSignature && (
        <div className="rounded-sm border-brutal border-ink bg-mint/30 p-3 text-sm">
          Your signature is <Emoji scale={1.3}>{me.signatureEmoji ?? ""}</Emoji> — it's yours forever
          and can't be changed.
        </div>
      )}
      {mode === "claim" && !me?.hasSignature && (
        <div className="text-sm text-gray-600">
          Pick your <strong>signature emoji</strong>. It's permanent, unique to you, and can't be
          changed later — choose wisely.
        </div>
      )}

      {/* search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or category…"
        className="w-full rounded-sm border-brutal border-ink px-3 py-2 text-sm focus:outline-none max-[700px]:text-[16px]"
      />

      {/* category tabs */}
      {!searching && (
        <div className="flex items-center gap-0.5 overflow-x-auto pb-0.5">
          {categories.map((c) => (
            <button
              key={c.category}
              type="button"
              title={c.category}
              onClick={() => setActiveCat(c.category)}
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-base transition-colors",
                c.category === currentCat
                  ? "bg-mint/40 ring-1 ring-ink/40"
                  : "opacity-55 hover:bg-mint/20 hover:opacity-90",
              )}
            >
              <Emoji scale={1.05}>{categoryIcon(c.category)}</Emoji>
            </button>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500">
        {searching ? `${totalShown} match${totalShown === 1 ? "" : "es"}` : `${total} emoji in the registry`}
      </div>

      {/* grid */}
      <div className="max-h-[60vh] overflow-y-auto pr-1">
        {visible.map((c) => (
          <div key={c.category} className="mb-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {c.category}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {c.emojis.map((e) => {
                const isSelected =
                  (mode === "claim" && pending === e.e) ||
                  (mode === "pick" && selectedEmoji === e.e);
                const blocked =
                  mode === "claim" && (e.taken || !!e.reserved || me?.hasSignature);
                const title = e.mine
                  ? "Your signature"
                  : e.taken
                    ? `Claimed by ${e.owner ?? "someone"}`
                    : e.reserved
                      ? `Reserved (${e.reserved.label ?? e.reserved.kind})`
                      : e.used
                        ? "Already used in a past AOTD"
                        : e.n || c.category;
                return (
                  <button
                    key={e.e}
                    type="button"
                    title={title}
                    onClick={() => handleClick(e)}
                    disabled={blocked && !e.mine}
                    className={cn(
                      "relative flex h-10 w-10 items-center justify-center rounded-sm border text-xl transition-all",
                      "border-ink/15 hover:bg-mint/30",
                      e.mine && "ring-2 ring-mint bg-mint/30",
                      (e.taken || e.reserved) &&
                        mode === "claim" &&
                        "opacity-35 cursor-not-allowed hover:bg-transparent",
                      isSelected && "ring-2 ring-sky bg-sky/30",
                      blocked && !e.mine && "cursor-not-allowed opacity-60",
                    )}
                  >
                    <Emoji scale={1.25}>{e.e}</Emoji>
                    {e.used && (
                      <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-gold"
                        title="Used in a past AOTD"
                      />
                    )}
                    {e.reserved && (
                      <span
                        className="absolute -left-0.5 -top-0.5 h-2 w-2 rounded-full bg-ink/50"
                        title={`Reserved (${e.reserved.label ?? e.reserved.kind})`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {searching && totalShown === 0 && (
          <div className="p-4 text-sm text-gray-500">No emoji match “{search}”.</div>
        )}
      </div>

      {/* claim confirm footer */}
      {mode === "claim" && pending && (
        <div className="flex items-center justify-between gap-3 rounded-sm border-brutal border-ink bg-lemon/40 p-3">
          <div className="text-sm">
            Claim <Emoji scale={1.4}>{pending}</Emoji> as your <strong>permanent</strong> signature?
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPending(null)} disabled={claiming}>
              Cancel
            </Button>
            <Button variant="default" size="sm" onClick={doClaim} loading={claiming}>
              Claim forever
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojiSelector;
